<script setup>
import { computed } from 'vue';
import { messageStamp } from 'shared/helpers/timeHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  automation: {
    type: Object,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['toggle', 'edit', 'delete', 'clone']);

const readableDate = date => messageStamp(new Date(date), 'LLL d, yyyy');
const readableDateWithTime = date =>
  messageStamp(new Date(date), 'LLL d, yyyy hh:mm a');

const toggle = () => {
  const { id, name, active } = props.automation;
  emit('toggle', { id, name, status: active });
};

// Full class name strings required for Tailwind JIT to include them
const EVENT_CONFIG = {
  conversation_created: {
    label: 'AUTOMATION.EVENTS.CONVERSATION_CREATED',
    icon: 'i-lucide-message-circle-plus',
    iconBg: 'bg-n-teal-3',
    text: 'text-n-teal-9',
  },
  conversation_updated: {
    label: 'AUTOMATION.EVENTS.CONVERSATION_UPDATED',
    icon: 'i-lucide-refresh-cw',
    iconBg: 'bg-n-amber-3',
    text: 'text-n-amber-9',
  },
  message_created: {
    label: 'AUTOMATION.EVENTS.MESSAGE_CREATED',
    icon: 'i-lucide-message-square-plus',
    iconBg: 'bg-n-blue-3',
    text: 'text-n-blue-9',
  },
  conversation_opened: {
    label: 'AUTOMATION.EVENTS.CONVERSATION_OPENED',
    icon: 'i-lucide-folder-open',
    iconBg: 'bg-n-iris-3',
    text: 'text-n-iris-9',
  },
  conversation_idle: {
    label: 'AUTOMATION.EVENTS.CONVERSATION_IDLE',
    icon: 'i-lucide-clock',
    iconBg: 'bg-n-ruby-3',
    text: 'text-n-ruby-9',
  },
};

const eventConfig = computed(
  () =>
    EVENT_CONFIG[props.automation.event_name] ?? {
      label: null,
      icon: 'i-lucide-zap',
      iconBg: 'bg-n-alpha-3',
      text: 'text-n-slate-10',
    }
);

const conditionsCount = computed(
  () => props.automation.conditions?.length ?? 0
);
const actionsCount = computed(() => props.automation.actions?.length ?? 0);
</script>

<template>
  <div
    class="group relative flex flex-col rounded-2xl border border-n-weak bg-n-solid-1 overflow-hidden hover:shadow-md hover:border-n-strong transition-all duration-150"
    :class="automation.active ? 'opacity-100' : 'opacity-60 hover:opacity-100'"
  >
    <!-- Top strip: event type + active dot + toggle -->
    <div
      class="flex items-center justify-between gap-2 px-4 py-3 border-b border-n-weak"
    >
      <div class="flex items-center gap-2 min-w-0">
        <div
          class="size-7 rounded-lg flex items-center justify-center shrink-0"
          :class="eventConfig.iconBg"
        >
          <Icon
            :icon="eventConfig.icon"
            class="size-3.5"
            :class="eventConfig.text"
          />
        </div>
        <span class="text-sm font-medium text-n-slate-11 truncate">
          {{
            eventConfig.label ? $t(eventConfig.label) : automation.event_name
          }}
        </span>
      </div>
      <div class="flex items-center gap-2 shrink-0">
        <span
          v-if="automation.active"
          class="size-2 rounded-full bg-green-500"
        />
        <woot-switch :model-value="automation.active" @input="toggle" />
      </div>
    </div>

    <!-- Body: name + description -->
    <div class="px-4 py-4 flex-1">
      <h3 class="text-base font-bold text-n-slate-12 line-clamp-1">
        {{ automation.name }}
      </h3>
      <p
        v-if="automation.description"
        class="text-sm text-n-slate-9 mt-1 line-clamp-2"
      >
        {{ automation.description }}
      </p>
    </div>

    <!-- Bottom strip: stats + hover actions + date -->
    <div
      class="flex items-center justify-between gap-2 px-4 py-3 border-t border-n-weak"
    >
      <div class="flex items-center gap-3">
        <span
          v-tooltip.top="$t('AUTOMATION.ADD.FORM.CONDITIONS.LABEL')"
          class="flex items-center gap-1 text-xs text-n-slate-9 tabular-nums"
        >
          <Icon icon="i-lucide-filter" class="size-3.5 text-n-slate-6" />
          {{ conditionsCount }}
        </span>
        <span
          v-tooltip.top="$t('AUTOMATION.ADD.FORM.ACTIONS.LABEL')"
          class="flex items-center gap-1 text-xs text-n-slate-9 tabular-nums"
        >
          <Icon icon="i-lucide-zap" class="size-3.5 text-n-slate-6" />
          {{ actionsCount }}
        </span>
        <span
          v-tooltip.top="$t('AUTOMATION.LIST.TRIGGER_COUNT_LABEL')"
          class="flex items-center gap-1 text-xs text-n-slate-9 tabular-nums"
        >
          <Icon icon="i-lucide-activity" class="size-3.5 text-n-slate-6" />
          {{ automation.trigger_count ?? 0 }}
        </span>
      </div>
      <div class="flex items-center gap-1.5">
        <!-- Hover actions -->
        <div
          class="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
        >
          <Button
            v-tooltip.top="$t('AUTOMATION.FORM.EDIT')"
            icon="i-lucide-pen"
            slate
            xs
            faded
            :is-loading="loading"
            @click="$emit('edit', automation)"
          />
          <Button
            v-tooltip.top="$t('AUTOMATION.CLONE.TOOLTIP')"
            icon="i-lucide-copy-plus"
            xs
            faded
            :is-loading="loading"
            @click="$emit('clone', automation)"
          />
          <Button
            v-tooltip.top="$t('AUTOMATION.FORM.DELETE')"
            icon="i-lucide-trash-2"
            xs
            ruby
            faded
            :is-loading="loading"
            @click="$emit('delete', automation)"
          />
        </div>
        <span
          class="text-xs text-n-slate-8 tabular-nums"
          :title="readableDateWithTime(automation.created_on)"
        >
          {{ readableDate(automation.created_on) }}
        </span>
      </div>
    </div>
  </div>
</template>
